// src/pages/HotelAgentCommissionsPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, InputNumber, Modal, Spin, Table, Typography } from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axiosInstance from "../../api/axiosInstance";
import { useAuth } from "../../context/AuthContext";
import {
  CardTitle,
  DetailList,
  PageHeader,
  RowActions,
  SectionCard,
  StatStrip,
  StatusTag,
  fmtDate,
  tablePagination,
  useFlatPage,
} from "../../components/ui";
import { IconRefresh } from "../../components/ui/icons";
import notify from "../../utils/notify";

const { Text } = Typography;

// ─── component ──────────────────────────────────────────────────────────────

const HotelAgentCommissionsPage = () => {
  useFlatPage();
  const { authData } = useAuth();
  const queryClient = useQueryClient();
  const hotelId = authData?.hotelId;

  const [defaultCommission, setDefaultCommission] = useState(null);
  const [editingDefault, setEditingDefault] = useState(false);
  const [overrideModalOpen, setOverrideModalOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [overrideValue, setOverrideValue] = useState(null);

  // ── queries ──────────────────────────────────────────────────────────────

  const hotelQuery = useQuery({
    queryKey: ["hotel-commissions-hotel", hotelId],
    enabled: !!hotelId,
    queryFn: async () => (await axiosInstance.get(`/hotel/${hotelId}`)).data,
  });

  useEffect(() => {
    if (hotelQuery.data) {
      setDefaultCommission(hotelQuery.data.defaultCommissionPercentage ?? null);
    }
  }, [hotelQuery.data]);

  const appsQuery = useQuery({
    queryKey: ["agent-applications-commissions", hotelId],
    enabled: !!hotelId,
    retry: false,
    queryFn: async () => {
      const res = await axiosInstance.get(
        `/HotelAgentApplications/hotels/${hotelId}/applications`
      );
      return Array.isArray(res.data) ? res.data : [];
    },
  });

  // ── mutations ─────────────────────────────────────────────────────────────

  const saveDefaultMutation = useMutation({
    mutationFn: async (value) => {
      const hotel = hotelQuery.data;
      const payload = {
        allowAgentAffiliation: hotel?.allowAgentAffiliation ?? true,
        autoApproveAgents: hotel?.autoApproveAgents ?? false,
        defaultCommissionPercentage: value,
      };
      return (
        await axiosInstance.put(
          `/HotelAgentApplications/hotels/${hotelId}/settings`,
          payload
        )
      ).data;
    },
    onSuccess: () => {
      notify.success("Default commission updated.");
      setEditingDefault(false);
      queryClient.invalidateQueries({ queryKey: ["hotel-commissions-hotel", hotelId] });
    },
    onError: (e) => notify.error(e?.response?.data?.message || "Failed to update commission."),
  });

  // NOTE: Per-agent commission override requires a backend endpoint
  // e.g. PUT /HotelAgentApplications/hotels/{hotelId}/applications/{appId}/commission
  // This mutation is a placeholder — wire to your actual endpoint when available.
  const overrideMutation = useMutation({
    mutationFn: async ({ applicationId, commissionPercentage }) => {
      // Replace this URL with your actual endpoint when backend is ready
      return (
        await axiosInstance.put(
          `/HotelAgentApplications/hotels/${hotelId}/applications/${applicationId}/commission`,
          { commissionPercentage }
        )
      ).data;
    },
    onSuccess: () => {
      notify.success("Commission override saved.");
      setOverrideModalOpen(false);
      setSelectedAgent(null);
      queryClient.invalidateQueries({ queryKey: ["agent-applications-commissions", hotelId] });
    },
    onError: (e) => notify.error(e?.response?.data?.message || "Override failed."),
  });

  // ── derived state ─────────────────────────────────────────────────────────

  const applications = useMemo(() => appsQuery.data ?? [], [appsQuery.data]);
  const hotel = hotelQuery.data;

  const approvedAgents = useMemo(() => {
    return applications.filter((a) => {
      const s = String(a.status ?? "").toLowerCase();
      return s.includes("approved") || s === "3";
    });
  }, [applications]);

  const allAgents = useMemo(() => {
    return applications.filter((a) => {
      const s = String(a.status ?? "").toLowerCase();
      // Show approved + pending to plan commissions ahead of approval
      return s.includes("approved") || s === "3" || s.includes("pending") || s === "1" || s === "2";
    });
  }, [applications]);

  const totalCommission = useMemo(() => {
    // Placeholder: sum of commission values across approved agents
    // Replace with actual booking revenue * commission when that data exists
    return null;
  }, []);

  const openOverride = (agent) => {
    setSelectedAgent(agent);
    setOverrideValue(agent.commissionPercentage ?? hotel?.defaultCommissionPercentage ?? null);
    setOverrideModalOpen(true);
  };

  const isApproved = (v) => {
    const s = String(v ?? "").toLowerCase();
    return s.includes("approved") || s === "3";
  };

  const initials = (name = "") =>
    String(name)
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((x) => x[0])
      .join("")
      .toUpperCase() || "A";

  const columns = [
    {
      title: "Agent",
      key: "agent",
      render: (_, row) => (
        <span className="hms-person">
          <span className="hms-avatar">{initials(row.agentName)}</span>
          <span className="hms-cell-stack">
            <span className="hms-cell-main">{row.agentName || "—"}</span>
            <span className="hms-cell-sub">{row.companyName || "—"}</span>
          </span>
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 140,
      render: (v) => <StatusTag status={isApproved(v) ? "available" : "pending"}>{isApproved(v) ? "Approved" : String(v)}</StatusTag>,
    },
    {
      title: "Commission",
      key: "commission",
      width: 220,
      render: (_, row) => {
        const val = row.commissionPercentage;
        const isOverride = val != null && val !== hotel?.defaultCommissionPercentage;
        return val != null ? (
          <span>
            <span className="hms-cell-main">{val}%</span>
            {isOverride && <span className="hms-pill">Custom</span>}
          </span>
        ) : (
          <span className="hms-muted">Default ({hotel?.defaultCommissionPercentage ?? "—"}%)</span>
        );
      },
    },
    {
      title: "Approved",
      dataIndex: "reviewedAtUtc",
      width: 140,
      render: (v) => fmtDate(v),
    },
    {
      title: "",
      key: "actions",
      align: "right",
      width: 150,
      render: (_, row) => (
        <RowActions
          actions={[
            { key: "override", label: "Set custom commission", icon: "edit", showLabel: true, onClick: () => openOverride(row) },
          ]}
        />
      ),
    },
  ];

  if (!hotelId) return <Alert type="warning" title="No hotel context." showIcon />;

  if (hotelQuery.isLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  const defaultPct = hotel?.defaultCommissionPercentage;
  const overrides = allAgents.filter((a) => a.commissionPercentage != null).length;

  return (
    <div className="hms-page cc-page">
      <PageHeader
        actions={
          <Button
            icon={<IconRefresh />}
            onClick={() => {
              queryClient.invalidateQueries({ queryKey: ["hotel-commissions-hotel", hotelId] });
              queryClient.invalidateQueries({ queryKey: ["agent-applications-commissions", hotelId] });
            }}
          >
            Refresh
          </Button>
        }
      />

      <div className="hms-stack">
        <SectionCard flush>
          <StatStrip
            items={[
              { label: "Default commission", value: defaultPct != null ? `${defaultPct}%` : "—" },
              { label: "Approved agents", value: approvedAgents.length, tone: "good" },
              { label: "With a custom rate", value: overrides },
            ]}
          />
        </SectionCard>

        <SectionCard
          title="Hotel default commission"
          extra={
            !editingDefault ? (
              <Button onClick={() => setEditingDefault(true)}>Edit</Button>
            ) : (
              <div className="hms-form-actions" style={{ marginTop: 0 }}>
                <Button onClick={() => setEditingDefault(false)}>Cancel</Button>
                <Button type="primary" loading={saveDefaultMutation.isPending} onClick={() => saveDefaultMutation.mutate(defaultCommission)}>
                  Save
                </Button>
              </div>
            )
          }
        >
          <div className="hms-grid-2">
            <div>
              <p className="hms-modal-note">
                This rate applies to every agent without a custom rate, and to future approvals. You can change it at any time.
              </p>
              {editingDefault ? (
                <InputNumber
                  min={0}
                  max={100}
                  addonAfter="%"
                  style={{ width: 180 }}
                  value={defaultCommission}
                  onChange={setDefaultCommission}
                  autoFocus
                />
              ) : (
                <div className="hms-summary-title" style={{ fontSize: "2rem" }}>
                  {defaultPct != null ? `${defaultPct}%` : <span className="hms-muted">Not set</span>}
                </div>
              )}
            </div>
            <div>
              <div className="hms-dsection-head" style={{ marginTop: 0 }}>
                <h4>How commissions work</h4>
              </div>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                <li>New agents get the hotel default when they are approved.</li>
                <li>Set a custom rate for one agent in the table below.</li>
                <li>Changing the default does not affect agents with a custom rate.</li>
              </ul>
            </div>
          </div>
        </SectionCard>

        <SectionCard flush title={<CardTitle count={allAgents.length}>Agent commission rates</CardTitle>}>
          {appsQuery.isError ? (
            <Alert type="warning" showIcon title="Could not load agent data." style={{ margin: 16 }} />
          ) : (
            <Table
              className="hms-table"
              rowKey="hotelAgentApplicationId"
              columns={columns}
              dataSource={allAgents}
              loading={appsQuery.isLoading}
              pagination={tablePagination("agent", { hideOnSinglePage: true })}
              locale={{
                emptyText: <div className="hms-empty">No agents yet. Approve an agent application to manage commissions here.</div>,
              }}
            />
          )}
        </SectionCard>

        <p className="hms-muted" style={{ margin: 0 }}>
          Revenue and payout tracking per agent will appear here once bookings are attributed to agents.
        </p>
      </div>

      <Modal
        title={`Custom commission: ${selectedAgent?.agentName || "Agent"}`}
        open={overrideModalOpen}
        onCancel={() => {
          setOverrideModalOpen(false);
          setSelectedAgent(null);
        }}
        onOk={() =>
          overrideMutation.mutate({
            applicationId: selectedAgent?.hotelAgentApplicationId,
            commissionPercentage: overrideValue,
          })
        }
        okText="Save rate"
        confirmLoading={overrideMutation.isPending}
        width={480}
        destroyOnHidden
      >
        <DetailList
          items={[
            { label: "Agent", value: selectedAgent?.agentName },
            { label: "Company", value: selectedAgent?.companyName },
            { label: "Hotel default", value: defaultPct != null ? `${defaultPct}%` : "—" },
          ]}
        />

        <div className="hms-field" style={{ marginTop: 16 }}>
          <label htmlFor="cc-override">Commission percentage</label>
          <InputNumber
            id="cc-override"
            min={0}
            max={100}
            addonAfter="%"
            style={{ width: "100%" }}
            value={overrideValue}
            onChange={setOverrideValue}
            placeholder={`Default: ${defaultPct ?? "—"}%`}
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            Leave blank to go back to the hotel default.
          </Text>
        </div>

        {overrideValue !== null && overrideValue !== defaultPct && (
          <Alert type="warning" showIcon title={`This is different from the hotel default (${defaultPct ?? "—"}%).`} />
        )}
      </Modal>
    </div>
  );
};

export default HotelAgentCommissionsPage;
